<svelte:options tag="kz-file" />

<script type="ts">

	/**
	 * バージョン定義
	 * ---------------------------------------------------------------------------------------------
	 */
	const KZ_FILE_VERSION = '0.1.12';

	/**
	 * モジュールロード
	 * ---------------------------------------------------------------------------------------------
	 */
	import { onMount, tick } from 'svelte';
	import { Afile } from "./kz-file-afile.class";
	import { sizeLabel, comma } from "./kz-functions";

	/**
	 * 属性定義
	 * ---------------------------------------------------------------------------------------------
	 */
	export let theme           : string = 'simple';
	export let files           : string = '[]';
	export let name            : string = '';
	export let fixfilenamebase : string = '';
	export let ap              : string = '';
	export let view            : boolean = undefined;
	export let debug           : boolean = undefined;
	export let width           : string = '80';
	export let height          : string = '50';
	export let accept          : string = '';
	export let addclass        : string = '';
	export let multiple        : number = undefined; // 数値を指定すると上限数が指定できる。最小数などは別途バリデータに任す。
	export let editable        : string = undefined; // キーのみ指定なら標準の編集機能、関数指定すると外部関数が使用できる。
	export let parallels       : number = 3; // 同時アップロード上限数
	export let cssfile         : string = ''; // シャドウDOM用スタイル上書き用CSSファイルパスを指定可能
	export let maxsize         : string = ''; // 最大容量を指定可能（１ファイルごと）ex. maxsize="10MB" ... (b, kb, mb, gb が利用可能)数字のみの場合はMB
	export let maxtotal        : string = ''; // 最大容量を指定可能（全体の合計数）※全体というのはコンポーネント単体内の、ということ。 ↑ 同上
	export let minsize         : string = ''; // 最小容量を指定可能（１ファイルごと）ex. minsize="500KB" ... (b, kb, mb, gb が利用可能)数字のみの場合はMB
	// export let mintotal        : string = ''; // 複数一変に選択しないといけなかったり挙動がスマートじゃなくなってしまう＋合計の最小値というケースは別のバリーデートでいいんじゃないか？そもそも必要とされなさそうなのでいったん未実装。
	export let onselect        : string = null; // フック関数指定：「ファイルを選択」ボタン押下時に実行
	export let onerror         : string = null; // フック関数指定：「ファイルを選択」ボタン押下時に実行
	export let beforeupload    : string = null; // フック関数指定：アップロード直前に実行
	export let afterupload     : string = null; // フック関数指定：アップロード直後に実行
	export let beforecancel    : string = null; // フック関数指定：取消直前に実行
	export let aftercancel     : string = null; // フック関数指定：取消直後に実行
	export let onchange        : string = null; // フック関数指定：filesの変更時に実行（アニメーションstateの変更は無視する。純粋にリストの更新時と削除チェックの有無をフックする）

	/**
	 * 内部変数定義
	 * ---------------------------------------------------------------------------------------------
	 */
	const SVG_SYMBOL_SAME_STRING_M64 = 'M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256';
	let inputfiles: FileList; // bind <input>'s FileList
	let inputfile_element: HTMLInputElement; // bind <input>'s element
	let afiles: Afile[];
	let total_filesize: number;
	let my_kz_file: Element; // <kz-file>要素本体
	let content: Element; // ShadowRoot直下のdivタグ
	let hidden: HTMLInputElement; // ShdowRoot外のデータ連帯用
	let is_modal: boolean = false;
	let is_dragging: boolean = false;
	let is_dragging_flag: boolean = false;
	let is_dragging_afile: boolean = false;
	let modal_afile: Afile;
	let afile_ul: HTMLUListElement; // LI要素のスライドアップのための高さ取り専用変数
	let upload_started: boolean = false;
	let maxnum: number;
	let counts: {
		all          : number,
		cued         : number,
		uploading    : number,
		temped       : number,
		saved        : number,
		saved_delete : number,
		error        : number,
		enabled      : number,
	}

	/**
	 * リアクティブ定義
	 * ---------------------------------------------------------------------------------------------
	 */
	$: files = JSON.stringify(afiles);
	$: if(hidden) hidden.value = files;
	$: { // afilesのstateごとのカウント、及び削除ボタンチェック状態による state=saved_delete の更新
		counts = { all:0, cued:0, uploading:0, temped:0, saved:0, saved_delete:0, error:0, enabled:0 };
		if(afiles) afiles.forEach(afile=>{
			// 削除チェックによる saved ↔ saved_delete の state 更新
			if(['saved', 'saved_delete'].includes(afile.state)) {
				afile.state = afile.checked_delete ? 'saved_delete' : 'saved'
				afiles = afiles;
				do_hook(onchange, null)
			}
			// 各種 state カウント
			counts[afile.state] ++;
			if('error'!==afile.state && 'saved_delete'!==afile.state) counts.enabled ++;
			counts.all ++;
		})
		if(my_kz_file) {
			Object.keys(counts).forEach(key=>{
				my_kz_file.setAttribute('count-'+key, String(counts[key]))
			})
		}
	}
	$: maxnum = undefined===multiple ? 1 : (multiple>0 ? Number(multiple) : null) // multiple指定がなければ最大数は１。数が指定されていればその数が最大数に。値なしの multiple 属性のみなら無制限の nullに。
	$: total_filesize = afiles
		? afiles
			.filter(afile=>['temped','saved'].includes(afile.state)) // アップ済みのファイルのみ対象
			.reduce((total:number, afile:Afile)=>{return total+afile.size}, 0)
		: 0
	;
	$: total_filesize_with_processing = afiles
		? afiles
			.filter(afile=>['cued','uploading','temped','saved'].includes(afile.state)) // アップ済みのファイルのみ対象＋キュー|アップ中のも含める
			.reduce((total:number, afile:Afile)=>{return total+afile.size}, 0)
		: 0
	;

	/**
	 * マウント時に実行
	 * ---------------------------------------------------------------------------------------------
	*/
	onMount(() => {
		// ホスト要素を探しておく
		document.querySelectorAll('kz-file').forEach(tag=>{
			if(tag.shadowRoot && tag.shadowRoot.contains(content)) my_kz_file = tag
		})

		// ホスト要素の次の位置にデータ連帯用 hidden 配置
		if(name) {
			hidden = document.createElement('input')
			hidden.type = 'hidden'
			hidden.name = name
			hidden.value = files
			if(my_kz_file.nextSibling) my_kz_file.nextSibling.remove()
			my_kz_file.parentNode.insertBefore(hidden, my_kz_file.nextSibling)
		}

		// files属性にセットされたJSON文字列からAfile配列をセット
		afiles = []
		files && JSON.parse(files).forEach( (file:object)=>{
			afiles.push(new Afile(file))
		})
	});

	// フック関数ベース
	const do_hook = (func:string, afile:Afile|null)=> {
		if(!func) return;
		setTimeout(()=>{
			Function('"use strict"; return (host, counts, afiles, afile, hidden)=>{'+func+'}')()(my_kz_file, counts, afiles, afile, hidden);
		}, 0)
	}

	/**
	 * アップロード関数定義
	 * ---------------------------------------------------------------------------------------------
	 */
	async function do_upload(event: DragEvent|Event) {

		// ファイルリスト用意
		let ifiles = (<DragEvent>event).dataTransfer
			? Array.from((<DragEvent>event).dataTransfer.files) // D&D時
			: Array.from(inputfiles) // 通常選択時
		;
		if(!ifiles.length) return
		if(undefined===multiple && ifiles.length>1) ifiles.splice(1) // 単数なのにD&Dで複数ファイルがドロップされた場合は最初の１個のみに。
		inputfile_element.value = null

		// 選択したファイルデータをリストに一括で追加する
		ifiles.forEach(inputfile=>{
			const afile = new Afile({
				state     : 'cued',
				filename  : fixfilenamebase ? fixfilenamebase+'.'+get_ext(inputfile.name) : inputfile.name,
				size      : inputfile.size,
				inputfile : inputfile,
			})
			afiles.push(afile);
		})
		afiles = afiles;

		// アップロードが既に始まっていたら何もしない（既に走ってる処理に任せる）
		if(upload_started) return;
		upload_started = true;

		// 実際のアップロード処理
		function afile_upload(afile:Afile, uploader_id:number) {

			return new Promise(result=>{

				// アップロード中にする
				afile.state = 'uploading';
				afiles = afiles

				// フォームデータ用意して１つずつ送信
				const form = new FormData()
				form.append('transfile', afile.inputfile)
				fetch(ap, {
					method: 'POST',
					cache: 'no-cache',
					body: form
				})
				.then(response => {
					if(404===response.status) throw new Error(response.statusText+' at '+response.url);
					return response.json()
				})
				.then(data => {
					if('success'===data.result) {
						setTimeout(()=>{
							afile.src = ap+(ap.match(/\?/)?'&':'?')+'temped='+data.file.temped_name+'.'+data.file.ext
							afile.temped_name = data.file.temped_name;
							if(data.file.width      ) afile.width       = data.file.width;
							if(data.file.height     ) afile.height      = data.file.height;
							if(data.file.aspect     ) afile.aspect      = data.file.aspect;
							if(data.file.size       ) afile.size        = data.file.size;
							if(data.file.before_size) afile.before_size = data.file.before_size;
							afile.state = 'temped';
							afiles = afiles;
							do_hook(onchange, null);
							result(true)
						}, 'simple'===theme ? 100 : 0)
					} else if('failed'===data.result) {
						setTimeout(()=>{
							afile.message = data.error.ja;
							afile.state = 'error';
							afiles = afiles;
							result(true)
						}, 'simple'===theme ? 100 : 0)
					}
					afile.anim_simple_loading_hide = true; // 100msのローディングバー消しアニメーション
					afiles = afiles;
				})
				.catch(error => {
					afile.state = 'error'
					afile.message='通信エラーが発生しました：'+error
					afiles = afiles;
					do_hook(onerror, afile); // フック実行：onerror
				});
			})
		}

		// アップロード処理を実行する関数を定義（Promise）
		const uploader = async (uploader_id:number)=>new Promise(async resolve=>{

			// キューされたファイルが１以上あれば１つづつアップ開始
			while(afiles.filter(afile=>'cued'===afile.state).length) {
				const afile = afiles.filter(afile=>'cued'===afile.state)[0]

				// 制限数に達していたらエラーメッセージをセット
				if(0<maxnum && maxnum<(counts.saved+counts.temped+1)) {
					afile.state = 'error'
					afile.message = `アップロードできるファイルは ${maxnum} 個までです。`
					afiles = afiles
					do_hook(onerror, afile); // フック実行：onerror
					continue;
				}

				// １ファイル容量制限チェック（最大）
				if(maxsize && afile.size > toByte(maxsize)) {
					afile.state = 'error'
					afile.message = `アップロードできる１ファイルの容量は ${maxsize.match(/^(\d+\.?\d*)$/) ? maxsize+'MB' : maxsize} までです。`
					afiles = afiles
					do_hook(onerror, afile); // フック実行：onerror
					continue;
				}

				// 全ファイルの総計容量制限チェック（最大）
				if(maxtotal && (total_filesize+afile.size) > toByte(maxtotal)) {
					afile.state = 'error'
					afile.message = `アップロードできる総容量は ${maxtotal.match(/^(\d+\.?\d*)$/) ? maxtotal+'MB' : maxtotal} までです。`
					afiles = afiles
					do_hook(onerror, afile); // フック実行：onerror
					continue;
				}

				// １ファイル容量制限チェック（最小）
				if(minsize && afile.size < toByte(minsize)) {
					afile.state = 'error'
					afile.message = `アップロードできる１ファイルの容量は ${minsize.match(/^(\d+\.?\d*)$/) ? minsize+'MB' : minsize} 以上です。`
					afiles = afiles
					do_hook(onerror, afile); // フック実行：onerror
					continue;
				}

				// アップロード処理
				await afile_upload(afile, uploader_id)

				// PHP側でエラー出てたらフック
				if('error'===afile.state) do_hook(onerror, afile); // フック実行：onerror

				// 再度、制限数に達していたらエラーメッセージをセット＋一時ファイルの削除
				if(0<multiple && multiple<(counts.temped+counts.saved)) {
					deleteTemped(afile.temped_name);
					afile.src=''
					afile.state = 'error'
					afile.message = `アップロードできるファイルは ${multiple} 個までです。`
					afiles = afiles
					do_hook(onerror, afile); // フック実行：onerror
					continue;
				}

				// 再度、全ファイルの総計容量制限チェック（最大）＋一時ファイルの削除
				if(maxtotal && total_filesize > toByte(maxtotal)) {
					deleteTemped(afile.temped_name);
					afile.src=''
					afile.state = 'error'
					afile.message = `アップロードできる総容量は ${maxtotal.match(/^(\d+\.?\d*)$/) ? maxtotal+'MB' : maxtotal} までです。`
					afiles = afiles
					do_hook(onerror, afile); // フック実行：onerror
					continue;
				}

			}
			resolve(true)
		});

		// フック実行：beforeupload
		await tick();
		do_hook(beforeupload, null);

		// 指定数のアップローダーたちを起動する（同時実行アップロード数）
		const uploaders = [];
		for (let i=0; i<parallels; i++) {
			uploaders.push(uploader(i))
		}

		// 並列実行開始
		await Promise.all(uploaders);

		// 完了
		upload_started = false;

		// フック実行：afterupload
		if(afterupload) {
			// 全ての処理を wait するはずだが、なぜか最後のアップロード処理が完全に表示がおわる前に走ってしまうのでちょいおくらせ。
			setTimeout(()=>{
				do_hook(afterupload, null);
				do_hook(onchange, null);
			}, 100)
		}
	}

	/**
	 * 「ファイルを選択」ボタンクリック時処理
	 * ---------------------------------------------------------------------------------------------
	 */
	function doSelectClick(event: Event) {
		// フック実行：onselect
		if(onselect) Function('"use strict"; return host=>{'+onselect+'}')()(my_kz_file);
		// ファイル選択ダイアログ起動
		inputfile_element.click();
	}
	// 「ファイルを選択」ボタンが disabled でもクリック時に実行したいので外側のBOXをクリック時に実行
	function doSelectOuterClick(event: Event) {
		// エラーのものはここで消す
		doCancel(afiles.filter(afile=>'error'===afile.state))
	}

	/**
	 * ファイルリンクのクリック時処理
	 * ---------------------------------------------------------------------------------------------
	 */
	function doClick(event: Event, afile: Afile) {
		if('noexist'===afile.state) {
			event.stopPropagation();
			event.preventDefault();
			return;
		}
		if(afile.is_media()) showModal(event, afile)
	}

	/**
	 * 「削除」チェックボックスクリック時
	 * ---------------------------------------------------------------------------------------------
	 */
	function clickDelete(event: Event, afile: Afile) {

		// 最大数が設定されていない場合は特に何もしない
		if(!maxnum) return;

		// 削除チェックを外す際に、最大数に達してしまう場合はチェックを外せないように。
		if('saved_delete'===afile.state) {
			if(counts.enabled+1>maxnum) {
				alert('削除チェックを外すには、先に不要なファイルを取り消す必要があります。'+(1!==maxnum ? `\n（最大アップロード数 ${maxnum} を超えてしまうため）` : ''))
				event.preventDefault();
			}
		}
	}

	/**
	 * モーダルビュー開く
	 * ---------------------------------------------------------------------------------------------
	 */
	function showModal(event: Event, afile: Afile) {
		event.preventDefault();
		modal_afile = afile
		is_modal = true;
		scrollable = false;
	}

	/**
	 * モーダルビュー閉じ
	 * ---------------------------------------------------------------------------------------------
	 */
	function modalClose() {
		is_modal = false;
		scrollable = true;
	}

	/**
	 * afile から対象の li 要素を取得する関数
	 * ---------------------------------------------------------------------------------------------
	 */
	async function afile_to_li(afile: Afile) {
		await tick();
		const index = afiles.indexOf(afile)
		if(undefined===index) return false;
		return <HTMLLIElement>afile_ul.querySelector('li:nth-child('+(index+1)+')');
	}

	/**
	 * 取り消し処理
	 * ---------------------------------------------------------------------------------------------
	 */
	function doCancel(cancel_afiles: Afile|Afile[]) {

		// 配列ではない場合はここで配列にしておく
		if(!Array.isArray(cancel_afiles)) cancel_afiles = [cancel_afiles]
		if(!cancel_afiles.length) return;

		// 削除する要素を回す
		cancel_afiles.forEach(async afile=>{

			// 一時ファイルの削除は先に走らせておく
			deleteTemped(afile.temped_name);

			// 該当のLIの高さを取得して style 属性で設定する。
			const afile_li = await afile_to_li(afile)
			if(afile_li) afile_li.setAttribute('style', 'height:'+String(afile_li.offsetHeight)+'px');
		})

		// タイムアウトゼロ ↓ にて ↑ が適用された後に処理を進める（でないと、スライドアップ時にカックンとなる）
		setTimeout(()=>{

			// 削除する要素を回してキャンセルアニメーション用CSSクラスを有効にするスイッチ
			if(Array.isArray(cancel_afiles)) {
				cancel_afiles.forEach(afile=>{
					afile.anim_afile_cancel = true;
					afiles = afiles;
				})
			}	

			// フック実行：beforecancel
			do_hook(beforecancel, null);

			// キャンセルアニメが0.2秒あるので、その後にリストから除去されるようにタイムアウト設定
			setTimeout(()=>{
				if(Array.isArray(cancel_afiles)) {
					cancel_afiles.forEach(async afile=>{
						const index = afiles.indexOf(afile)
						if(undefined!==index) afiles.splice(index, 1)
						// afiles = afiles
						// await tick();
					})
				}

				// 表示更新
				afiles = afiles

				// フック実行：aftercancel
				do_hook(aftercancel, null);
				do_hook(onchange, null);

			}, 200)
		}, 0)
	}

	/**
	 * アップロード済みファイルをサーバ側から削除
	 * ---------------------------------------------------------------------------------------------
	 */
	function deleteTemped(temped_name: string) {
		if(temped_name) {
			fetch(ap, {
				method: 'DELETE',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({temped_files: [temped_name]})
			})
			.then(response => response.json())
			.then(data => {
				// console.log(data)
			})
		}
	}

	/**
	 * 編集機能実行
	 * ---------------------------------------------------------------------------------------------
	 */
	function doEditor(afile: Afile) {
		// カスタム関数を実行
		if(editable) {
			do_hook(editable, afile) // フック用関数だけど文字列で書かれた関数を実行するのに流用する。
		}
		// 標準関数を実行?
		else {
			;
		}
	}

	/**
	 * ウィンドウをスクロールさせないようにする関数
	 * 参考：https://stackoverflow.com/questions/60031642/how-to-add-parameters-to-a-window-event-mouse-wheel-in-svelte
	 * ---------------------------------------------------------------------------------------------
	 */
	let scrollable = true;
	function wheel (node: any, options: any) {
		let { scrollable } = options;
		const handler = (e:Event)=>{ if (!scrollable) e.preventDefault() };
		node.addEventListener('wheel', handler, { passive: false });
		return {
			update(options: any) { scrollable = options.scrollable },
			destroy() { node.removeEventListener('wheel', handler, { passive: false }) }
		};
	};

	/**
	 * キー入力時処理
	 * ---------------------------------------------------------------------------------------------
	 */
	function keydown(e: KeyboardEvent) {
		'Escape'===e.key && modalClose()
	}

	/**
	 * maxsize属性, maxtotal属性, minsize属性, mintotal属性 で設定した値をバイト値に変換。
	 * ex. <kz-file maxsize="10MB">
	 * ex. if(MAX < toByte(maxsize)) ... 10*1024*1024バイト
	 * ---------------------------------------------------------------------------------------------
	 */
	function toByte(size: string) {
		let byte: number;
		let matches: RegExpMatchArray | null;
		if (matches = size.match(/^(\d+\.?\d*)b$/i))            byte = parseFloat(matches[1]) * (1024 ** 0);
		else if (matches = size.match(/^(\d+\.?\d*)kb$/i))      byte = parseFloat(matches[1]) * (1024 ** 1);
		else if (matches = size.match(/^(\d+\.?\d*)(?:mb)?$/i)) byte = parseFloat(matches[1]) * (1024 ** 2); // 単位なしの場合はMBとして
		else if (matches = size.match(/^(\d+\.?\d*)gb$/i))      byte = parseFloat(matches[1]) * (1024 ** 3);
		return byte;
	}

	/**
	 * ファイル名から拡張子を返す（ドットは除外）
	 * ---------------------------------------------------------------------------------------------
	 */
	function get_ext(filename: string) {
		const matches = filename.match(/([^\.]+)$/);
		return matches ? matches[1] : false;
	}

	/**
	 * 表示用ファイル名（ダウンロードファイル名）を返す。fixfilenamebase 属性が指定されていればそれを利用したい。
	 * ---------------------------------------------------------------------------------------------
	 */
	function show_public_filename(afile: Afile) {
		return fixfilenamebase
			? fixfilenamebase+'.'+get_ext(afile.show_filename())
			: afile.show_filename()
		;
	}

	/**
	 * filesの内容でリロードする（外部のVueなどで内容を書き換えた場合などに使用）
	 * ---------------------------------------------------------------------------------------------
	 * ex. document.querySelector('kz-file[name=image]').reload(); とか外部からの実行が可能
	 */
	export async function reload() {
		await tick()
		afiles = []
		if(files) {
			JSON.parse(files).forEach( (file)=>{
				afiles.push(new Afile(file))
			})
		}
	}
</script>

<!-- HOST ELEMENT -->
<svelte:window use:wheel={{scrollable}} />
<svelte:body on:keydown={keydown} />

<!-- CUSTOM ELEMENT -->
{#if cssfile}
<link rel="stylesheet" href={cssfile}>
{/if}
<div id="kz-file-block" class={addclass}>
	<div
		class={'theme-'+theme}
		class:drag-over={is_dragging}
		class:view={undefined!==view}
		bind:this={content}
		on:dragenter|stopPropagation|preventDefault={e=>{if(undefined===view && !is_dragging_afile){is_dragging_flag=true}}}
		on:dragover|stopPropagation|preventDefault ={e=>{if(undefined===view && !is_dragging_afile){is_dragging_flag=false; is_dragging=true}}}
		on:dragleave|stopPropagation|preventDefault={e=>{if(undefined===view && !is_dragging_afile){if(is_dragging_flag){is_dragging_flag=false}else{is_dragging=false}}}}
		on:drop|stopPropagation|preventDefault     ={e=>{if(undefined===view && !is_dragging_afile){is_dragging=false; do_upload(e)}}}
		on:dragstart={e=>{if(undefined===view){is_dragging_afile=true}}}
		on:dragend  ={e=>{if(undefined===view){is_dragging_afile=false}}}
	>
		{#if afiles && afiles.length}
		<ul bind:this={afile_ul}>
			{#each afiles as afile, index (afile)}
			{#if afile.state}
			{#if undefined===view || (undefined!==view && ['temped','saved','saved_delete','noexist'].includes(afile.state))}
			<li class="state-{afile.state}" class:afile-hide={afile.anim_afile_cancel}>
				{#if 'thumb'===theme}
				<div class="thumbnail"
						style:width={width+'px'}
						style:height={height+'px'}
						style:min-width={width+'px'}
						style:min-height={height+'px'}
						class:stripe={['uploading','cued'].includes(afile.state)}
						class:stripe-wait={['cued'].includes(afile.state)}
						class:is_hover={afile.is_hover}
						class:checker-bg-mini={['temped','saved'].includes(afile.state) && afile.is_image()}
						class:white-bg={['temped','saved'].includes(afile.state) && !afile.is_image()}
					>
						{#if ['error','noexist'].includes(afile.state)}
						<svg><use xlink:href="#{afile.get_icon_type()}"/></svg>
						{:else if afile.src}
						<!-- svelte-ignore a11y-mouse-events-have-key-events -->
						<a href={afile.src}
							download={afile.is_media() ? null : show_public_filename(afile)}
							on:click={e=>{doClick(e, afile)}}
							on:mouseover={()=>{afile.is_hover=true}}
							on:mouseout={()=>{afile.is_hover=false}}
						>
							{#if afile.is_image()}
							<img src={afile.src} alt={show_public_filename(afile)} on:load={e=>afile.image_loaded=true}/>
							{:else}
							{@html afile.show_icon()}
							{/if}
						</a>
						{/if}
					</div>
				{/if}
			<div class="info">
				<div class="fileinfo" title={afile.src}>
						{#if afile.src && 'noexist'!==afile.state}
						<!-- svelte-ignore a11y-mouse-events-have-key-events -->
						<a href={afile.src}
							class:is_hover={afile.is_hover}
							download={afile.is_media() ? null : show_public_filename(afile)}
							on:click={e=>{doClick(e, afile)}}
							on:mouseover={()=>{afile.is_hover=true}}
							on:mouseout={()=>{afile.is_hover=false}}
						>
							<span class="filename">
								{@html 'simple'===theme ? afile.show_icon() : ''}
								<span>{show_public_filename(afile)}</span>
							</span>
							{#if !isNaN(afile.size)}<span class="filesize">({afile.show_size()})</span>{/if}
						</a>
						{:else}
						<!-- svelte-ignore a11y-missing-attribute -->
						<a>
							<span class="filename">
								{@html 'simple'===theme ? afile.show_icon() : ''}
								<span>{show_public_filename(afile)}</span>
							</span>
							{#if !isNaN(afile.size)}<span class="filesize">({afile.show_size()})</span>{/if}
						</a>
						{/if}
					</div>
				<div class="control">
						{#if undefined===view}
							{#if undefined!==editable}
							<div class="editable" on:click={e=>doEditor(afile)}>
								<svg><use xlink:href="#caution"/></svg> <span>編集</span>
							</div>
							{/if}
							{#if ['cued','uploading','temped','error'].includes(afile.state)}
							<div class="cancel" on:click={()=>doCancel(afile)}>
								<svg><use xlink:href="#cross"/></svg> <span>取消</span>
							</div>
							{:else if 'saved'===afile.state || 'saved_delete'===afile.state}
							<div class="delete">
								<label><input type="checkbox" bind:checked={afile.checked_delete} on:click={e=>{clickDelete(e, afile)}}> <span>削除</span></label>
							</div>
							{/if}
						{:else if 'saved_delete'===afile.state}
							<div class="delete">
								削除します。
							</div>
						{:else if 'noexist'===afile.state}
							<div class="noexist">
								ファイルが存在しません。
							</div>
						{/if}
					</div>
				</div>
				{#if 'simple'===theme}
				<div
					class="loading-bar stripe stripe-mini"
					class:loading-bar-hide={afile.anim_simple_loading_hide}
					class:stripe-wait={'cued'===afile.state}
				></div>
				{/if}
				{#if afile.message}
				<div class="message message-{afile.state}">
					<div>
						<div>
							<svg><use xlink:href="#info"/></svg>
							{#if 'error'===afile.state}
							<span>アップロードエラー</span>
							{:else}
							<span>メッセージ</span>
							{/if}
						</div>
						<p>{afile.message}</p>
					</div>
				</div>
				{/if}
			</li>
			{/if}
			{:else}
			<li class="init-error">
				<svg><use xlink:href="#caution"/></svg>
				<span>ファイル情報に state キーがありません。</span>
			</li>
			{/if}
			{/each}
		</ul>
		{/if}
		{#if undefined===view}
		<div class="button" on:click={doSelectOuterClick}>
			<button
				on:click|stopPropagation={doSelectClick}
				disabled={
					(!ap)||
					(undefined===multiple && counts.enabled>=1)||
					(0<multiple && multiple<=counts.enabled)
				}
			><slot>ファイルを選択</slot></button>
			{#if !ap}
			<span class="caution" on:click={e=>alert('ap 属性が指定されていないためファイルをアップロードできません。')}>
				<svg><use xlink:href="#caution"/></svg>
			</span>
			{/if}
			<input type="file"
				bind:this={inputfile_element}
				bind:files={inputfiles}
				on:change={do_upload}
				accept={accept?accept:undefined}
				multiple={undefined!==multiple && 1!==Number(multiple)}
			>
		</div>
		{/if}
	</div>

	{#if undefined!==debug}
	<!-- DEBUG BOX -->
	<div id="debug">
		<textarea readonly>{files && files.replaceAll(/[\r\n\t]/g, '')}</textarea>
		<ul>
			<li><code>アップ待ち：<span>{counts.cued}</span></code></li>
			<li><code>アップ中：<span>{counts.uploading}</span></code></li>
			<li><code>一時保存済み：<span>{counts.temped}</span></code></li>
			<li><code>保存済み：<span>{counts.saved}</span></code></li>
			<li><code>保存済み削除予約：<span>{counts.saved_delete}</span></code></li>
			<li><code>エラー：<span>{counts.error}</span></code></li>
			<li><code>有効数：<span>{counts.enabled}</span></code></li>
			<li><code>アイテム総数：<span>{counts.all}</span></code></li>
			<li><code>ファイルサイズ合計：<span>{sizeLabel(total_filesize)}</span></code></li>
		</ul>
		<small>&lt;kz-file&gt; version <span>{KZ_FILE_VERSION}</span></small>
		<!-- <button on:click={()=>reload()}>reload from files</button> -->
	</div>
	{/if}

	<!-- MODAL BOX -->
	<div id="modal" class:modal-show={is_modal}>
		<div class="modal-bg" on:click={modalClose}>
			<div class="modal-content" on:click|stopPropagation>
				<button class="close" on:click={modalClose}>
					<svg><use xlink:href="#cross"/></svg>
				</button>
				<div id="item" class:checker-bg={modal_afile && modal_afile.is_image()}>
					{#if modal_afile}
					{#key modal_afile.src}
						{#if modal_afile.is_image()}
						<img src={modal_afile.src} alt="*">
						{:else if modal_afile.is_video()}
						<!-- svelte-ignore a11y-media-has-caption -->
						<video controls src={modal_afile.src}></video>
						{:else if modal_afile.is_audio()}
						<audio controls src={modal_afile.src}></audio>
						{:else}
						<p class="unsupported">
							未対応のファイルタイプです<br>
							{modal_afile.src}
						</p>
						{/if}
						{#if modal_afile.src}
						<div id="download">
							<a href={modal_afile.src} download={show_public_filename(modal_afile)}>
								<svg><use xlink:href="#downfile"/></svg>
								<span>{show_public_filename(modal_afile)}</span>
							</a>
						</div>
						{/if}
					{/key}
					{:else}
					<div class="error">No data exists...</div>
					{/if}
				</div>
			</div>
		</div>
	</div>

</div>

<!-- SVG ICONS -->
<svg xmlns="http://www.w3.org/2000/svg" style="display:none">
	<!--! Font Awesome Pro 6.2.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. -->
	<symbol id="word"       viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM111 257.1l26.8 89.2 31.6-90.3c3.4-9.6 12.5-16.1 22.7-16.1s19.3 6.4 22.7 16.1l31.6 90.3L273 257.1c3.8-12.7 17.2-19.9 29.9-16.1s19.9 17.2 16.1 29.9l-48 160c-3 10-12.1 16.9-22.4 17.1s-19.8-6.2-23.2-16.1L192 336.6l-33.3 95.3c-3.4 9.8-12.8 16.3-23.2 16.1s-19.5-7.1-22.4-17.1l-48-160c-3.8-12.7 3.4-26.1 16.1-29.9s26.1 3.4 29.9 16.1z'}/></symbol>
	<symbol id="excel"      viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM155.7 250.2L192 302.1l36.3-51.9c7.6-10.9 22.6-13.5 33.4-5.9s13.5 22.6 5.9 33.4L221.3 344l46.4 66.2c7.6 10.9 5 25.8-5.9 33.4s-25.8 5-33.4-5.9L192 385.8l-36.3 51.9c-7.6 10.9-22.6 13.5-33.4 5.9s-13.5-22.6-5.9-33.4L162.7 344l-46.4-66.2c-7.6-10.9-5-25.8 5.9-33.4s25.8-5 33.4 5.9z'}/></symbol>
	<symbol id="powerpoint" viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM136 240h68c42 0 76 34 76 76s-34 76-76 76H160v32c0 13.3-10.7 24-24 24s-24-10.7-24-24V368 264c0-13.3 10.7-24 24-24zm68 104c15.5 0 28-12.5 28-28s-12.5-28-28-28H160v56h44z'}/></symbol>
	<symbol id="pdf"        viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM64 224H88c30.9 0 56 25.1 56 56s-25.1 56-56 56H80v32c0 8.8-7.2 16-16 16s-16-7.2-16-16V320 240c0-8.8 7.2-16 16-16zm24 80c13.3 0 24-10.7 24-24s-10.7-24-24-24H80v48h8zm72-64c0-8.8 7.2-16 16-16h24c26.5 0 48 21.5 48 48v64c0 26.5-21.5 48-48 48H176c-8.8 0-16-7.2-16-16V240zm32 112h8c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16h-8v96zm96-128h48c8.8 0 16 7.2 16 16s-7.2 16-16 16H304v32h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H304v48c0 8.8-7.2 16-16 16s-16-7.2-16-16V304 240c0-8.8 7.2-16 16-16z'}/></symbol>
	<symbol id="archive"    viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM96 48c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16zm-6.3 71.8c3.7-14 16.4-23.8 30.9-23.8h14.8c14.5 0 27.2 9.7 30.9 23.8l23.5 88.2c1.4 5.4 2.1 10.9 2.1 16.4c0 35.2-28.8 63.7-64 63.7s-64-28.5-64-63.7c0-5.5 .7-11.1 2.1-16.4l23.5-88.2zM112 336c-8.8 0-16 7.2-16 16s7.2 16 16 16h32c8.8 0 16-7.2 16-16s-7.2-16-16-16H112z'}/></symbol>
	<symbol id="image"      viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM128 256c0 17.7-14.3 32-32 32s-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32zm88 32c5.3 0 10.2 2.6 13.2 6.9l88 128c3.4 4.9 3.7 11.3 1 16.5s-8.2 8.6-14.2 8.6H216 176 128 80c-5.8 0-11.1-3.1-13.9-8.1s-2.8-11.2 .2-16.1l48-80c2.9-4.8 8.1-7.8 13.7-7.8s10.8 2.9 13.7 7.8l12.8 21.4 48.3-70.2c3-4.3 7.9-6.9 13.2-6.9z'}/></symbol>
	<symbol id="video"      viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM64 288c0-17.7 14.3-32 32-32h96c17.7 0 32 14.3 32 32v96c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V288zM300.9 397.9L256 368V304l44.9-29.9c2-1.3 4.4-2.1 6.8-2.1c6.8 0 12.3 5.5 12.3 12.3V387.7c0 6.8-5.5 12.3-12.3 12.3c-2.4 0-4.8-.7-6.8-2.1z'}/></symbol>
	<symbol id="audio"      viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zm2 226.3c37.1 22.4 62 63.1 62 109.7s-24.9 87.3-62 109.7c-7.6 4.6-17.4 2.1-22-5.4s-2.1-17.4 5.4-22C269.4 401.5 288 370.9 288 336s-18.6-65.5-46.5-82.3c-7.6-4.6-10-14.4-5.4-22s14.4-10 22-5.4zm-91.9 30.9c6 2.5 9.9 8.3 9.9 14.8V400c0 6.5-3.9 12.3-9.9 14.8s-12.9 1.1-17.4-3.5L113.4 376H80c-8.8 0-16-7.2-16-16V312c0-8.8 7.2-16 16-16h33.4l35.3-35.3c4.6-4.6 11.5-5.9 17.4-3.5zm51 34.9c6.6-5.9 16.7-5.3 22.6 1.3C249.8 304.6 256 319.6 256 336s-6.2 31.4-16.3 42.7c-5.9 6.6-16 7.1-22.6 1.3s-7.1-16-1.3-22.6c5.1-5.7 8.1-13.1 8.1-21.3s-3.1-15.7-8.1-21.3c-5.9-6.6-5.3-16.7 1.3-22.6z'}/></symbol>
	<symbol id="other"      viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM112 256H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16z'}/></symbol>
	<symbol id="downfile"   viewBox="0 0 384 512"><path d={SVG_SYMBOL_SAME_STRING_M64+' 0zM216 232V334.1l31-31c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-72 72c-9.4 9.4-24.6 9.4-33.9 0l-72-72c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l31 31V232c0-13.3 10.7-24 24-24s24 10.7 24 24z'}/></symbol>
	<symbol id="caution"    viewBox="0 0 512 512"><path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224c0-17.7-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32s32-14.3 32-32z"/></symbol>
	<symbol id="cross"      viewBox="0 0 320 512"><path d="M310.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L160 210.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L114.7 256 9.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 301.3 265.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L205.3 256 310.6 150.6z"/></symbol>
	<symbol id="info"       viewBox="0 0 512 512"><path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0S0 114.6 0 256S114.6 512 256 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-144c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32z"/></symbol>
</svg>

<style lang="scss">
$color-temped: #F2994A;
$color-saved: #2F80ED;
$color-error: #EB5757;
$color-noexist: #a3b8df;
$color-delete: #EB5757;
$color-gray: #a3a3a3;
$color-black: #555;
$color-hover: #00f;

.stripe {
	background: linear-gradient(-45deg, #ccc 25%, #eee 25%, #eee 50%, #ccc 50%, #ccc 75%, #eee 75%, #eee);
	background-size: 30px 30px;
	animation: anime_stripe 0.5s infinite linear;
}
.stripe-mini {
	background-size: 10px 10px;
	animation: anime_stripe 1.0s infinite linear;
}
.stripe-wait {
	animation: none;
}
@keyframes anime_stripe {
	0% {background-position-x: 0;}
	100% {background-position-x: 30px;}
}
:host {
	* { box-sizing: border-box; }
	> #kz-file-block {
		> div {
			position: relative;
			> ul {
				list-style: none;
				margin: 0;
				padding: 0;
				> li {
					display: flex;
					align-items: center;
					gap: 0 10px;
					padding: 2px 2px 14px 2px;
					margin-bottom: 10px;
					border-bottom: solid 1px #ddd;
					overflow: hidden;
					transition: all 0.2s 0s ease;
					> .thumbnail {
						transition: filter 0.1s 0s ease;
						outline: 2px solid $color-gray;
						border-radius: 3px;
						overflow: hidden;
						> a {
							width: 100%;
							height: 100%;
							display: flex;
							justify-content: center;
							align-items: center;
							> img {
								width: 100%;
								height: 100%;
								object-fit: cover;
							}
							svg {
								width: 75%;
								height: 75%;
							}
						}
						&.is_hover {
							filter: brightness(1.2);
						}
					}
					> .info {
						> .fileinfo {
							color: $color-gray;
							font-size: 0.9rem;
							font-weight: bold;
							vertical-align: text-bottom;
							word-break: break-all;
							svg {
								fill: $color-gray;
							}
							a.is_hover {
								filter: brightness(1.2);
							}
							.filesize {
								font-weight: normal;
								font-size: 0.75em;
							}
						}
						> .control {
							font-size: 0.9rem;
							vertical-align: text-bottom;
							display: flex;
							gap: 0 25px;
							> div {
								svg {
									width: 16px;
									height: 16px;
								}
								&.cancel {
									color: $color-black;
									svg {
										fill: $color-black;
									}
									&:hover {
										color: #EB5757;
										text-decoration: underline;
										svg {
											fill: #EB5757;
										}
									}
								}
							}
							>div.cancel {
								cursor: pointer;
								display: inline-block;
								white-space: nowrap;
								svg, span {
									vertical-align: middle;
								}
							}
							>div.delete {
								white-space: nowrap;
								label {
									cursor: pointer;
								}
								label:hover {
									text-decoration: underline;
									color: #EB5757;
								}
							}
						}
					}
					> .message {
						flex-basis: 100%;
						background-color: rgb(223, 230, 236);
						margin-top: 5px;
						padding: 10px;
						font-size: 12px;
						> div {
							padding: 0 10px 0 8px;
							border-left: 3px solid rgb(25, 77, 121);
							color: rgb(25, 77, 121);
							> div {
								font-size: 14px;
								margin: 0;
								padding: 0;
								font-weight: bold;
								> svg {
									width: 20px;
									height: 20px;
									fill: rgb(25, 77, 121);
									vertical-align: middle;
								}
								> span {
									vertical-align: middle;
								}
							}
							> p {
								margin: 5px 0 0 0;
								padding: 0;
							}
						}
					}
				}
				>li.state-temped {
					> .thumbnail {
						outline-color: $color-temped;
						> a {
							svg {
								fill: $color-temped;
							}
						}
					}
					> .info {
						> .fileinfo {
							a {
								color: $color-temped;
								svg {
									fill: $color-temped;
								}
							}
						}
					}
				}
				>li.state-saved ,
				>li.state-saved_delete {
					> .thumbnail {
						outline-color: $color-saved;
						> a {
							svg {
								fill: $color-saved;
							}
						}
					}
					> .info {
						> .fileinfo {
							a {
								color: $color-saved;
								svg {
									fill: $color-saved;
								}
							}
						}
					}
				}
				>li.state-saved_delete {
					> .thumbnail {
						outline-color: $color-gray;
						a {
							img {
								filter: grayscale(100%) opacity(0.5);
							}
							svg {
								fill: $color-gray;
							}
						}
					}
					> .info {
						> .fileinfo {
							a {
								color: $color-gray;
								text-decoration: underline line-through;
								svg {
									fill: $color-gray;
								}
							}
						}
						> .control {
							> .delete {
								color: $color-delete;
								font-weight: bold;
							}
						}
					}
				}
				>li.state-error ,
				>li.state-noexist {
					flex-wrap: wrap;
					> .thumbnail {
						display: flex;
						justify-content: center;
						align-items: center;
						outline-color: $color-error;
						background: #fff;
						> svg {
							fill: $color-error;
							width: 75%;
							height: 75%;
						}
					}
					> .info {
						> .fileinfo {
							color: $color-error;
							svg {
								fill: $color-error;
							}
						}
						> .control {
							> .noexist {
								color: $color-noexist;
								font-weight: bold;
							}
						}
					}
					> .message {
						background-color: rgba($color-error, .1);
						> div {
							border-left: 3px solid $color-error;
							color: $color-error;
							> div {
								> svg {
									fill: $color-error;
								}
							}
						}
					}
				}
				>li.state-noexist {
					> .thumbnail {
						outline-color: $color-noexist;
						svg {
							fill: $color-noexist;
						}
					}
					> .info {
						> .fileinfo {
							color: $color-noexist;
							svg {
								fill: $color-noexist;
							}
						}
					}
				}
				li.init-error {
					display: block;
					font-size: 14px;
					color: $color-error;
					svg {
						width: 25px;
						height: 20px;
						fill: $color-error;
						vertical-align: middle;
					}
					span {
						vertical-align: middle;
					}
				}
				li.afile-hide {
					height: 0 !important;
					padding-top: 0 !important;
					padding-bottom: 0 !important;
					margin-top: 0 !important;
					margin-bottom: 0 !important;
					visibility: hidden !important;
					opacity: 0.0;
				}
			}
			> .button {
				display: inline-block;
				button:disabled {
					pointer-events: none;
				}
				span.caution {
					svg {
						width: 20px;
						height: 20px;
						vertical-align: middle;
						fill: orangered;
						cursor: pointer;
						transition: all 0.1s 0s ease;
						&:hover {
							filter: brightness(1.6);
						}
					}
				}
				input {
					display: none;
				}
			}
		}
		> div.theme-simple {
			display: flex;
			flex-direction: row-reverse;
			justify-content: flex-end;
			gap: 0 20px;
			> ul {
				> li {
					display: block;
					padding: 1px 0 10px 0;
					> .info {
						display: flex;
						gap: 0 10px;
						> .fileinfo {
							.filename {
								svg {
									width: 20px;
									height: 20px;
									vertical-align: middle;
								}
								span {
									vertical-align: middle;
								}
							}
						}
					}
					margin-bottom: 0;
					border-bottom: none;
				}
				> li.state-cued ,
				> li.state-uploading {
					> .loading-bar {
						display: block;
						border: 1px solid $color-gray;
						border-radius: 3px;
						height: 5px;
						margin-top: 3px;
						transition: all 0.1s 0s ease;
					}
					> .loading-bar-hide {
						height: 0px;
						border: 0px;
						margin-top: 0px;
					}
				}
			}
		}
		> div.view {
			> ul {
				> li:last-child {
					border-bottom: none;
					padding-bottom: 2px;
					margin-bottom: 0;
				}
			}
		}
		.button {
			word-break: keep-all;
			& > button {
				cursor: pointer;
			}
		}
		#modal {
			visibility: hidden;
			position: fixed;
			z-index: 9999;
			top: 0;
			left: 0;
			opacity: 0.0;
			transition: all 0.15s 0s ease;
			.modal-bg {
				width: 100vw;
				height: 100vh;
				display: flex;
				align-items: center;
				justify-content: center;
				background: rgba(0, 0, 0, .7);
				.modal-content {
					max-width: 80%;
					max-height: 80%;
					button.close {
						position: absolute;
						right: 40px;
						top: 30px;
						border: none;
						background: none;
						cursor: pointer;
						svg {
							width: 40px;
							fill: #eee;
							height: 40px;
						}
						&:hover {
							svg {
								fill: #fff;
							}
						}
					}
					#item {
						transition: transform 0.15s 0s ease;
						transform: scale(1.05) rotate(0deg);
						display: flex;
						img ,
						video ,
						audio {
							max-width: 80vw;
							max-height: 80vh;
							box-shadow: 5px 5px 30px 0 rgb(0 0 0 / 30%);
						}
						audio {
							box-shadow: none;
						}
						#download {
							position: absolute;
							bottom: -35px;
							white-space: pre;
							a {
								display: inline-flex;
								color: #ddd;
								font-size: 12px;
								align-content: center;
								align-items: center;
								gap: 0 6px;
								svg {
									width: 20px;
									height: 20px;
									fill: #ddd;
								}
								&:hover {
									color: #fff;
									svg {
										fill: #fff;
									}
								}
							}
						}
					}
				}
			}
			&.modal-show {
				visibility: visible;
				opacity: 1.0;
				backdrop-filter: blur(3px) grayscale(30%);
				.modal-content {
					#item {
						transition: transform 0.15s 0s ease-out;
						transform: scale(1) rotate(0deg);
					}
				}
			}
			.error {
				color: #ddd;
				font-style: italic;
				letter-spacing: 0.5px;
				font-weight: normal;
			}
		}
		.checker-bg {
			background: linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%),linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%);
			background-color: #fff;
			background-size: 20px 20px;
			background-position: 0 0, 10px 10px;
		}
		.checker-bg-mini {
			@extend .checker-bg;
			background-size: 10px 10px;
			background-position: 0 0, 5px 5px;
		}
		.white-bg {
			background: #fff;
		}
		.drag-over::before {
			display: block;
			content: "";
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			background: rgba(255, 255, 0, 0.2);
			outline: solid 15px rgba(255, 255, 0, 0.2);
			border-radius: 10px;
		}
		#debug {
			textarea {
				display: block;
				width: 100%;
				height: auto;
				margin-top: 10px;
				padding: 5px;
				background: #333;
				color: #eee;
				font-size: 12px;
				border-radius: 3px;
				overflow-y: auto;
				line-height: 1.2;
				word-break: break-all;
			}
			ul {
				list-style: none;
				margin: 0;
				margin-top: 1px;
				padding: 0;
				display: flex;
				flex-wrap: wrap;
				gap: 1px;
				li {
					background-color: #555;
					color: #eee;
					font-size: 10px;
					padding: 2px 4px;
					border-radius: 4px;
					margin-bottom: 0;
					span {
						font-size: 13px;
						color: #7aff00;
						font-weight: bold;
					}
				}
			}
			small {
				font-style: italic;
				color: #555;
			}
		}
	}
}
</style>
